/**
 * APCard & APTemplateCard — AP landing page card components.
 *
 * APCard (Aktiv tab):
 *   Vertical card (aspect-[3/4]). Large faded background icon.
 *   Normal state: name, description, type label (bottom separator).
 *   Hover — always slides:
 *     · All requirements met → "Heute im Blick" preview stats.
 *     · Any unmet           → "Voraussetzungen" checklist + bottom separator.
 *   Lock badge top-right when showRequirements (incl. Config AP unconfigured).
 *
 * APTemplateCard (Alle tab):
 *   Same proportions, dashed border, "Vorlage" badge, static.
 */

import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { IconLock, IconMessage } from "@tabler/icons-react"
import clsx from "clsx"
import type {
  APStoreData,
  ApType,
  AutoPilotDef,
  AutoPilotTemplate,
  LockRequirement,
} from "./registry"

// ── Type labels ───────────────────────────────────────────────────────────────

const AP_TYPE_LABEL_KEYS: Record<ApType, TranslationKeys> = {
  configurator: "QMS/AP_TYPE/CONFIGURATOR",
  importer: "QMS/AP_TYPE/IMPORTER",
  exporter: "QMS/AP_TYPE/EXPORTER",
  monitor: "QMS/AP_TYPE/MONITOR",
}

// ── Dot colours (inline style — Tailwind shadow composition unreliable) ───────

const DOT_STYLE = {
  green: { background: "rgb(74 222 128)" }, // green-400
  yellow: { background: "rgb(250 204 21)" }, // yellow-400
  red: { background: "rgb(239 68 68)" }, // red-500
} as const

function RequirementDot({
  req,
  storeData,
}: {
  req: LockRequirement
  storeData: APStoreData
}) {
  const met = req.isMet(storeData)
  const style = met ? DOT_STYLE.green : DOT_STYLE[req.pendingColor]
  return (
    <div
      className="mt-[10px] h-2.5 w-2.5 shrink-0 rounded-full"
      style={style}
    />
  )
}

function requirementTextClass(req: LockRequirement, met: boolean): string {
  if (met) return "text-white/60"
  if (req.pendingColor === "yellow") return "text-white/80"
  return "text-grey5/55"
}

// ── Active instance card (Aktiv tab) ─────────────────────────────────────────

interface APCardProps {
  ap: AutoPilotDef
  storeData: APStoreData
  onSelect: (apId: string) => void
}

export function APCard({ ap, storeData, onSelect }: APCardProps) {
  const { translate } = useTranslation()
  const isUnlocked = ap.unlockCondition({
    dbConfigured: storeData.dbConfigured,
  })
  const Icon = ap.icon
  const notifications = ap.getNotifications(storeData)
  const previewStats = ap.getPreviewStats(storeData)
  const typeLabel = translate(AP_TYPE_LABEL_KEYS[ap.apType])

  const showRequirements =
    ap.lockRequirements.length > 0 &&
    !ap.lockRequirements.every((r) => r.isMet(storeData))

  return (
    <div
      className={clsx(
        "group relative transition-opacity duration-200",
        !isUnlocked && "opacity-45",
      )}
    >
      <button
        onClick={isUnlocked ? () => onSelect(ap.id) : undefined}
        className={clsx(
          "relative aspect-3/4 w-full overflow-hidden rounded-2xl border bg-grey1",
          isUnlocked
            ? "cursor-pointer border-grey4 ring-1 ring-white/6 transition-colors duration-200 hover:border-grey5/30 hover:ring-white/10"
            : "cursor-default border-grey4/30",
        )}
      >
        {/* ── Faded background icon (normal state) ──────────── */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Icon
            size={130}
            className={clsx(
              "text-white",
              isUnlocked ? "opacity-[0.04]" : "opacity-[0.03]",
            )}
          />
        </div>

        {/* ── Top-right badge ────────────────────────────────── */}
        {showRequirements && (
          <div className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-grey4 bg-grey3">
            <IconLock size={15} className="text-white" />
          </div>
        )}
        {!showRequirements && isUnlocked && notifications > 0 && (
          <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-full bg-yellow1 py-1 pl-1.5 pr-2 text-[11px] font-bold text-grey1 ring-2 ring-grey1">
            <IconMessage size={13} />
            {notifications}
          </div>
        )}

        {/* ── Normal state ───────────────────────────────────── */}
        <div className="absolute inset-0 flex flex-col p-6 transition-transform duration-300 ease-in-out group-hover:-translate-y-full">
          <div className="flex-1" />
          <div className="space-y-3">
            <p
              className={clsx(
                "text-2xl font-semibold leading-snug",
                isUnlocked ? "text-white" : "text-white/75",
              )}
            >
              {translate(ap.name as TranslationKeys)}
            </p>
            <p
              className={clsx(
                "line-clamp-2 h-13 text-base font-light leading-relaxed",
                isUnlocked ? "text-grey5" : "text-grey5/60",
              )}
            >
              {translate(ap.description as TranslationKeys)}
            </p>
          </div>
          <div
            className={clsx(
              "mt-6 border-t pt-3",
              isUnlocked ? "border-grey4/60" : "border-grey4/30",
            )}
          >
            <span
              className={clsx(
                "text-xs font-medium uppercase tracking-[0.12em]",
                isUnlocked ? "text-grey5/50" : "text-grey5/30",
              )}
            >
              {typeLabel}
            </span>
          </div>
        </div>

        {/* ── Hover panel ────────────────────────────────────── */}
        <div className="absolute inset-0 flex translate-y-full flex-col bg-grey1 p-6 transition-transform duration-300 ease-in-out group-hover:translate-y-0">
          {!showRequirements ? (
            /* Preview stats */
            <>
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-white">
                {translate("QMS/LANDING/TODAY_IN_FOCUS")}
              </p>
              <div className="mt-3 border-t border-grey4/60" />
              {previewStats.length > 0 ? (
                <div className="flex flex-1 flex-col justify-center gap-7">
                  {previewStats.map((stat, i) => (
                    <div key={i} className="flex flex-col gap-1">
                      <span className="text-sm font-light text-grey5">
                        {translate(stat.label as TranslationKeys)}
                      </span>
                      <span className="text-5xl font-bold tabular-nums leading-none text-white">
                        {stat.value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center">
                  <p className="text-center text-base text-grey5/50">
                    {translate("QMS/COMMON/NO_DATA")}
                  </p>
                </div>
              )}
            </>
          ) : (
            /* Requirements checklist */
            <>
              {/* Faded lock icon background */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <IconLock size={130} stroke={1} className="text-white" />
              </div>

              <p className="relative text-xs font-medium uppercase tracking-[0.12em] text-white">
                {translate("QMS/LANDING/REQUIREMENTS")}
              </p>

              {/* Separator beneath the header — mirrors the type-label separator */}
              <div className="relative mt-3 border-t border-grey4/60" />

              <div className="relative mt-5 flex flex-col gap-3">
                {ap.lockRequirements.map((req, i) => {
                  const met = req.isMet(storeData)
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <RequirementDot req={req} storeData={storeData} />
                      <span
                        className={clsx(
                          "text-lg font-light leading-snug",
                          requirementTextClass(req, met),
                        )}
                      >
                        {translate(req.label as TranslationKeys)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </button>
    </div>
  )
}

// ── Template card (Alle tab) ──────────────────────────────────────────────────

interface APTemplateCardProps {
  template: AutoPilotTemplate
}

export function APTemplateCard({ template }: APTemplateCardProps) {
  const { translate } = useTranslation()
  const Icon = template.icon
  const typeLabel = translate(AP_TYPE_LABEL_KEYS[template.apType])

  return (
    <div
      className="relative aspect-3/4 w-full overflow-hidden rounded-2xl border border-dashed border-grey4/50 bg-grey1/20"
      title={translate("QMS/LANDING/TEMPLATE_ACTIVATION_SOON")}
    >
      {/* Faded background icon */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <Icon size={130} className="text-white opacity-[0.025]" />
      </div>

      {/* Vorlage badge */}
      <div className="absolute right-3 top-3">
        <span className="rounded-full border border-grey4/50 bg-grey3/40 px-2 py-0.5 text-[9px] font-medium uppercase tracking-widest text-grey5/50">
          {translate("QMS/LANDING/TEMPLATE_BADGE")}
        </span>
      </div>

      {/* Content */}
      <div className="absolute inset-0 flex flex-col p-6 opacity-40">
        <div className="flex-1" />
        <div className="space-y-3">
          <p className="text-2xl font-semibold leading-snug text-white">
            {translate(template.name as TranslationKeys)}
          </p>
          <p className="line-clamp-2 h-13 text-base font-light leading-relaxed text-grey5">
            {translate(template.description as TranslationKeys)}
          </p>
        </div>
        <div className="mt-6 border-t border-grey4/60 pt-3">
          <span className="text-xs font-medium uppercase tracking-[0.12em] text-grey5/50">
            {typeLabel}
          </span>
        </div>
      </div>
    </div>
  )
}
